import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookAnsweredData,
  BookQuestion,
  BookSlot,
  BookingDetails as fetchBookingDetails,
} from "../../store/BookingSlice";
import { RootState } from "../../store/store";
import {
  AllBookingDetails,
  BookingLists,
} from "../../interfaces/BookingList";
import ReadMoreComponent from "../../components/ReadMore";
import { Calendar, DateObject } from "react-multi-date-picker";
import moment from "moment";
import { ApexChart } from "../../components/Charts/LineChart";
import { generatePDF } from "../../components/GeneratePdf";

type HandleDateChangeType = (
  selectedDate: DateObject | DateObject[] | null,
  options: {
    validatedValue: string | string[];
    input: HTMLElement;
    isTyping: boolean;
  }
) => false | void;

const formatSlotTime = (time: any) => {
  if (!time) return "";
  if (typeof time === "string" && /^\d{1,2}:\d{2}/.test(time)) {
    return moment(time, ["HH:mm", "HH:mm:ss", "hh:mm A"]).format("HH:mm");
  }
  const m = moment.utc(time);
  return m.isValid() ? m.format("HH:mm") : String(time);
};

export default function BookingsDetails() {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";
  const dispatch = useDispatch<any>();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [values, setValues] = useState<Date[]>([]);
  const [popups, setPopups] = useState<{ [key: string]: boolean }>({});
  const [radioValue, setRadiovalue] = useState("");
  const [answers, setAnswers] = useState<
    {
      id: string;
      ans: string;
      selectedOption?: string | null;
      questionAskedId?: string;
    }[]
  >([]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    if (index === 0 && bookingId) {
      dispatch(fetchBookingDetails(bookingId));
    }
  };

  useEffect(() => {
    if (bookingId) {
      dispatch(fetchBookingDetails(bookingId));
    }
  }, [dispatch, bookingId]);

  const { data: bookingData, loading: isLoading } = useSelector(
    (state: RootState) => state.booking
  );
  const bookingDetail: any = bookingData || ([] as AllBookingDetails[]);

  const programTitle =
    (bookingDetail.booking as BookingLists)?.program?.title ||
    bookingDetail.booking?.program?.title;
  const programId = bookingDetail.booking?.program?.id;

  const selectedDates =
    bookingDetail.sessions?.reduce((acc: any, session: any) => {
      acc.push(new Date(session.startDate));
      return acc;
    }, []) || [];

  const activeDates: Date[] =
    bookingDetail.counsellingBookings?.reduce((acc: Date[], booking: any) => {
      if (booking.counselling && booking.counselling.date) {
        acc.push(new Date(booking.counselling.date));
      }
      return acc;
    }, []) || [];

  const handleDateChange: HandleDateChangeType = (selectedDate) => {
    const convertedDates = selectedDate
      ? Array.isArray(selectedDate)
        ? selectedDate.map((dateObject) => dateObject.toDate())
        : [selectedDate.toDate()]
      : [];

    setValues(convertedDates);
    const formattedDates = convertedDates.map((date) =>
      moment(date).format("YYYY-MM-DD")
    );

    if (formattedDates.length > 0) {
      openPopup(formattedDates[0]);
    }
  };

  const openPopup = (dateKey: string) => {
    setPopups((prev) => ({ ...prev, [dateKey]: true }));
  };

  const closePopup = (dateKey: string) => {
    setPopups((prev) => ({ ...prev, [dateKey]: false }));
  };

  const changeSelection = (e: ChangeEvent<HTMLInputElement>) => {
    setRadiovalue(e.target.value);
  };

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!radioValue) return;

    dispatch(BookSlot(radioValue))
      .then(() => {
        window.location.reload();
      })
      .catch((err: { message: any }) => {
        if (err instanceof Error) {
          console.error(err.message);
        }
      });
  };

  const mergedArray = [
    ...(bookingDetail?.sessions ?? []),
    ...(bookingDetail?.counsellingBookings ?? []),
  ];

  // Sort mergedArray by start date/time
  const sortedArray = [...mergedArray].sort((a, b) => {
    const dateA = moment(a.startDate ?? a.counselling?.date);
    const dateB = moment(b.startDate ?? b.counselling?.date);
    return dateA.valueOf() - dateB.valueOf();
  });

  const handleQuestionsSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    dispatch(BookQuestion({ answers, programId })).then(() => {
      setActiveTab(1);
      setAnswers([]);
      dispatch(BookAnsweredData(programId));
    });
  };

  const handleRadioChange = (
    id: string,
    questionAskedId: string,
    ans: string,
    value: string
  ) => {
    const updatedAnswers = answers.map((answer) => {
      if (answer.questionAskedId === questionAskedId) {
        return {
          ...answer,
          selectedOption: value,
          ans: ans,
          questionAskedId: questionAskedId,
        };
      }
      return answer;
    });

    if (
      updatedAnswers.some(
        (answer) => answer.questionAskedId === questionAskedId
      )
    ) {
      setAnswers(updatedAnswers);
    } else {
      setAnswers([
        ...updatedAnswers,
        { id, ans: ans, selectedOption: value, questionAskedId: questionAskedId },
      ]);
    }
  };

  const handleTextareaChange = (
    id: string,
    questionAskedId: string,
    value: string
  ) => {
    const updatedAnswers = answers.map((answer) => {
      if (answer.questionAskedId === questionAskedId) {
        return { ...answer, ans: value, questionAskedId: questionAskedId };
      }
      return answer;
    });

    if (
      updatedAnswers.some(
        (answer) => answer.questionAskedId === questionAskedId
      )
    ) {
      setAnswers(updatedAnswers);
    } else {
      setAnswers([
        ...updatedAnswers,
        { id, ans: value, questionAskedId: questionAskedId },
      ]);
    }
  };

  useEffect(() => {
    if (bookingDetail?.booking?.program?.id) {
      dispatch(BookAnsweredData(programId));
    }
  }, [dispatch, programId, bookingDetail?.booking?.program?.id]);

  const allAnswers: any = useSelector((state: RootState) => {
    return state.booking.data1;
  });

  const handleButtonClick = () => {
    const paymentDetails = {
      date: bookingDetail.booking?.paymentDate,
      amount: bookingDetail.booking?.amount,
      paymentReferenceId: bookingDetail.booking?.paymentReferenceId,
      programName: bookingDetail.booking?.program?.title,
    };
    generatePDF(paymentDetails);
  };

  return (
    <div className="w-full pb-16">
      {/* Top Breadcrumb & Course Header */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-xs mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to="/bookings"
            className="p-2.5 rounded-xl bg-gray-50 text-[#95A8B1] hover:text-[#037897] hover:bg-[#EBF7F9] transition-colors shrink-0 mt-1"
            title={t("bookingDetails.backToBookings", "Back to Bookings")}
          >
            <svg
              width="22"
              height="18"
              viewBox="0 0 25 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.999702 10.0001L23.4082 10.0001"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.0381 19L0.999992 10.0007L10.0381 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>

          <div>
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Link
                to="/bookings"
                className="text-xs font-semibold text-gray-400 hover:text-[#037897] transition-colors"
              >
                {t("bookings.title", "My Bookings")}
              </Link>
              <span className="text-gray-300 text-xs">/</span>
              <span className="bg-[#EBF7F9] text-[#037897] text-xs font-bold px-2.5 py-0.5 rounded-full">
                {t("bookings.enrolled", "Enrolled Program")}
              </span>
            </div>

            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#041B44] tracking-tight min-h-[32px] flex items-center">
              {isLoading && !programTitle ? (
                <span className="inline-block h-7 sm:h-8 w-48 sm:w-72 bg-gray-200 animate-pulse rounded-lg" />
              ) : (
                programTitle || t("bookingDetails.title", "My Booking Details")
              )}
            </h1>
          </div>
        </div>

        {/* Quick Actions / Receipt Download */}
        {bookingDetail.booking?.paymentDate && (
          <div className="flex items-center gap-3 shrink-0 self-end md:self-auto">
            <button
              onClick={handleButtonClick}
              className="flex items-center gap-2 bg-[#037897]/10 hover:bg-[#037897] text-[#037897] hover:text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 cursor-pointer shadow-xs"
              title={t("bookingDetails.downloadReceipt", "Download Receipt (PDF)")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{t("bookingDetails.downloadReceipt", "Download Receipt (PDF)")}</span>
            </button>
          </div>
        )}
      </div>

      <div className="w-full flex flex-col gap-8">
        {/* Section 1: Counselling Calendar + Statistics Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {bookingDetail.booking?.program?.hasCounselling !== false && (
            <div className="lg:col-span-5 flex flex-col">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col justify-between flex-1 min-h-[440px]">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="uppercase font-bold text-base sm:text-lg text-[#020220] tracking-wide">
                      {t("bookingDetails.counsellingSlots", "Counselling slots")}
                    </h2>
                    <span className="text-sm font-bold text-[#037897] bg-[#EBF7F9] px-3 py-1 rounded-full">
                      {bookingDetail.counsellingBookings
                        ? bookingDetail.counsellingBookings.length
                        : 0}{" "}
                      / {bookingDetail.booking?.program?.totalCounsellings || 0}
                    </span>
                  </div>

                  <div className="w-full flex justify-center py-2">
                    <Calendar
                      key="uniqueKey"
                      minDate={new Date()}
                      highlightToday={false}
                      onChange={
                        handleDateChange as unknown as (
                          selectedDate: DateObject | DateObject[] | null
                        ) => false | void
                      }
                      className="day-contents"
                      mapDays={({ date }) => {
                        let className;
                        const strDate = moment(date.toDate()).format(
                          "YYYY-MM-DD"
                        );

                        selectedDates.forEach((selectDate: any) => {
                          if (
                            moment(selectDate).format("YYYY-MM-DD") === strDate
                          ) {
                            className = "active";
                          }
                        });
                        activeDates.forEach((activeDate) => {
                          if (
                            moment(activeDate).format("YYYY-MM-DD") === strDate
                          ) {
                            className = "active reserved";
                          }
                        });
                        if (className) return { className };
                      }}
                    />
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[#037897] text-center mt-4 font-medium bg-[#F0F9FB] p-2.5 rounded-xl">
                  {t("bookingDetails.selectDatePrompt", "Select a highlighted date to book your 1-on-1 session.")}
                </p>
              </div>

              {/* Counselling Slot Selection Modals */}
              {bookingDetail.counsellings?.counselingSessions &&
                bookingDetail.counsellings.counselingSessions.map(
                  (counselling: any) => (
                    <div key={counselling.date}>
                      {popups[counselling.date] && (
                        <div className="popup-overlay z-[999] p-4">
                          <div className="w-full max-w-lg bg-white rounded-2xl p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto">
                            <button
                              onClick={() => closePopup(counselling.date)}
                              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-700 text-lg font-bold rounded-full hover:bg-gray-100 transition-colors"
                            >
                              ✕
                            </button>

                            <form onSubmit={handleSubmit}>
                              <h3 className="text-lg sm:text-xl font-bold text-[#041B44] mb-1">
                                {t("bookingDetails.availableSlots", "Available Slots")}
                              </h3>
                              <p className="text-sm text-gray-500 mb-6">
                                {t("bookingDetails.dateLabel", "Date:")}{" "}
                                <span className="font-semibold text-gray-800">
                                  {moment
                                    .utc(counselling.date)
                                    .format("DD/MM/YYYY")}
                                </span>
                              </p>

                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                                {counselling.slots.map((slot: any) => (
                                  <div key={slot.id}>
                                    <input
                                      type="radio"
                                      id={slot.id}
                                      name="counsellingSlot"
                                      className="hidden"
                                      value={slot.id}
                                      onChange={changeSelection}
                                      checked={radioValue === slot.id}
                                      disabled={slot.isBooked === true}
                                    />
                                    <label
                                      htmlFor={slot.id}
                                      className={`w-full flex items-center justify-center py-3 px-2 rounded-xl text-sm font-semibold border transition-all ${slot.isBooked === true
                                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through"
                                        : radioValue === slot.id
                                          ? "bg-[#037897] text-white border-[#037897] shadow-md ring-2 ring-[#037897]/30"
                                          : "bg-white text-[#041B44] border-gray-200 hover:border-[#037897] hover:bg-[#F0F9FB] cursor-pointer"
                                        }`}
                                    >
                                      {formatSlotTime(slot.time)}
                                    </label>
                                  </div>
                                ))}
                              </div>

                              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                  type="button"
                                  onClick={() => closePopup(counselling.date)}
                                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  {t("bookingDetails.cancel", "Cancel")}
                                </button>
                                <button
                                  type="submit"
                                  disabled={!radioValue}
                                  className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-[#B4804A] hover:bg-[#9a6c3d] text-white shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {t("bookingDetails.confirmBooking", "Confirm Booking")}
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
            </div>
          )}

          {/* Statistics Chart */}
          <div
            className={
              bookingDetail.booking?.program?.hasCounselling !== false
                ? "lg:col-span-7 flex flex-col"
                : "lg:col-span-12 flex flex-col"
            }
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 flex flex-col flex-1 min-h-[440px]">
              <h2 className="uppercase font-bold text-base sm:text-lg text-[#020220] tracking-wide mb-4">
                {t("bookingDetails.statisticsTitle", "Statistics & Health Tracking")}
              </h2>
              <div className="flex-1 w-full min-h-[300px]">
                <ApexChart AnserData={allAnswers} />
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Questionnaires Tabs (Pending / Answered) */}
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          {/* Custom Responsive Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto overflow-y-hidden scrollbar-hide">
            <button
              type="button"
              onClick={() => handleTabChange(0)}
              className={`py-3 px-4 sm:px-6 text-sm sm:text-base font-bold uppercase tracking-wider transition-colors border-b-2 shrink-0 ${activeTab === 0
                ? "border-[#037897] text-[#037897]"
                : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
            >
              {t("bookingDetails.pendingTab", "Pending Questionnaire")}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange(1)}
              className={`py-3 px-4 sm:px-6 text-sm sm:text-base font-bold uppercase tracking-wider transition-colors border-b-2 shrink-0 ${activeTab === 1
                ? "border-[#037897] text-[#037897]"
                : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
            >
              {t("bookingDetails.answeredTab", "Answered History")}
            </button>
          </div>

          {/* Tab 0: Pending Questions */}
          {activeTab === 0 && (
            <form onSubmit={handleQuestionsSubmit} className="flex flex-col gap-6">
              {bookingDetail?.questions && bookingDetail.questions.length > 0 ? (
                <>
                  <div className="max-h-[480px] sm:max-h-[540px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar flex flex-col gap-4">
                    {bookingDetail.questions.map(
                      (question: any, questionIndex: number) => (
                        <div
                          key={questionIndex}
                          className="p-4 sm:p-5 rounded-xl bg-[#F9FAFB] border border-gray-100 flex flex-col gap-3"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <span className="font-bold text-[#037897] text-base shrink-0">
                                Q{questionIndex + 1}.
                              </span>
                              <h3 className="text-base sm:text-lg font-semibold text-[#263238]">
                                {question.text}
                              </h3>
                            </div>
                            <span className="text-xs text-[#78909C] bg-white px-2.5 py-1 rounded-md border border-gray-200 self-start sm:self-auto shrink-0">
                              {moment.utc(question.askedOn).format("DD/MM/YYYY")}
                            </span>
                          </div>

                          <div className="mt-2">
                            {question.type === "Text" ? (
                              <textarea
                                className="w-full border border-[#DAE2E7] rounded-xl bg-white p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#037897]"
                                rows={3}
                                placeholder={t("bookingDetails.answerPlaceholder", "Write your answer here...")}
                                name={question.questionAskedId}
                                defaultValue={
                                  answers.find(
                                    (answer) =>
                                      answer.questionAskedId ===
                                      question.questionAskedId
                                  )?.ans || ""
                                }
                                onChange={(e) =>
                                  handleTextareaChange(
                                    question.id,
                                    question.questionAskedId,
                                    e.target.value
                                  )
                                }
                              ></textarea>
                            ) : (
                              <div className="flex flex-wrap gap-2 sm:gap-3">
                                {question?.options &&
                                  question.options.map((option: any) => (
                                    <div key={option.id}>
                                      <input
                                        type="radio"
                                        id={option.id + question.questionAskedId}
                                        name={question.questionAskedId}
                                        className="hidden peer"
                                        value={option.id}
                                        onChange={() =>
                                          handleRadioChange(
                                            question.id,
                                            question.questionAskedId,
                                            option.title,
                                            option.id
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={
                                          option.id + question.questionAskedId
                                        }
                                        className="inline-block cursor-pointer text-xs sm:text-sm font-medium px-4 py-2.5 rounded-full border border-gray-200 bg-white text-[#546E7A] peer-checked:bg-[#037897] peer-checked:text-white peer-checked:border-[#037897] transition-all hover:bg-[#F0F9FB]"
                                      >
                                        {option.title}
                                      </label>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <button
                    type="submit"
                    className="self-start px-8 py-3.5 bg-[#037897] hover:bg-[#025c74] font-bold uppercase text-sm text-white rounded-xl shadow-md transition-colors"
                  >
                    {t("bookingDetails.saveAnswers", "Save Answers")}
                  </button>
                </>
              ) : (
                <div className="text-center py-8 text-[#78909C] text-sm sm:text-base">
                  {t("bookingDetails.noPendingQuestions", "No pending questions for today.")}
                </div>
              )}
            </form>
          )}

          {/* Tab 1: Answered History */}
          {activeTab === 1 && (
            <div className="flex flex-col gap-6">
              {allAnswers && allAnswers.length > 0 ? (
                <div className="max-h-[480px] sm:max-h-[540px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar flex flex-col gap-4">
                  {allAnswers.map((answer: any, answerIndex: number) => (
                    <div
                      key={answerIndex}
                      className="p-4 sm:p-5 rounded-xl bg-[#F9FAFB] border border-gray-100 flex flex-col gap-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-start gap-2">
                          <span className="font-bold text-[#037897] text-base shrink-0">
                            Q{answerIndex + 1}.
                          </span>
                          <h3 className="text-base sm:text-lg font-semibold text-[#263238]">
                            {answer.questionText}
                          </h3>
                        </div>
                        <span className="text-xs text-[#78909C] bg-white px-2.5 py-1 rounded-md border border-gray-200 self-start sm:self-auto shrink-0">
                          {t("bookingDetails.askedOn", "Asked:")} {moment.utc(answer.askedOn).format("DD/MM/YYYY")}
                        </span>
                      </div>

                      <div className="mt-2">
                        {answer.questionType === "Text" ? (
                          <div className="w-full border border-gray-200 rounded-xl bg-white p-3.5 text-sm text-[#263238]">
                            {answer.ans || t("bookingDetails.noTextProvided", "No text provided")}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            {answer?.questionOptions &&
                              answer.questionOptions.map((option: any) => (
                                <span
                                  key={option.id}
                                  className={`inline-block text-xs sm:text-sm font-medium px-4 py-2.5 rounded-full border ${option.id === answer.selectedOption
                                    ? "bg-[#041B44] text-white border-[#041B44]"
                                    : "bg-white text-gray-400 border-gray-200 opacity-60"
                                    }`}
                                >
                                  {option.title}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#78909C] text-sm sm:text-base">
                  {t("bookingDetails.noAnswerHistory", "No previous answers recorded yet.")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 3: Online Sessions Timeline */}
        <div className="w-full">
          <h2 className="uppercase font-bold text-lg sm:text-2xl text-[#020220] tracking-wide mb-6">
            {t("bookingDetails.sessionsTimeline", "Program Sessions & Timeline")}
          </h2>

          <div className="w-full flex flex-col gap-4">
            {sortedArray.map((session, sessionIndex) => (
              <div key={sessionIndex} className="w-full">
                {/* 1. Counselling Item */}
                {session.counselling && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl shadow-sm border border-gray-100 gap-4">
                    {/* Date Block */}
                    <div className="flex sm:flex-col items-center justify-between sm:justify-center sm:w-28 border-b sm:border-b-0 sm:border-r border-gray-100 pb-3 sm:pb-0 sm:pr-4 shrink-0">
                      <span className="text-2xl sm:text-3xl font-bold text-[#B4804A]">
                        {moment.utc(session.counselling.date).format("DD")}
                      </span>
                      <span className="text-xs sm:text-sm font-semibold uppercase text-gray-500">
                        {new Date(session.counselling.date ?? "").toLocaleDateString(
                          currentLocale,
                          { month: "short" }
                        )}
                      </span>
                    </div>

                    {/* Middle Content */}
                    <div className="flex-1 sm:px-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-[#FDF6E9] text-[#B4804A]">
                          {t("bookingDetails.oneOnOne", "1-on-1 Counselling")}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-[#041B44]">
                        {t("bookingDetails.personalisedConsultation", "Personalised Consultation")}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#78909C]">
                        {t("bookingDetails.counsellingDescription", "Direct video call session with your healthcare practitioner.")}
                      </p>
                    </div>

                    {/* Time & Join Button */}
                    <div className="flex sm:flex-col items-center justify-between sm:justify-center sm:w-44 border-t sm:border-t-0 sm:border-l border-gray-100 pt-3 sm:pt-0 sm:pl-4 gap-3 shrink-0">
                      <span className="text-xs sm:text-sm font-semibold text-gray-700">
                        {formatSlotTime(session.counselling.time)}{" "}
                        -{" "}
                        {formatSlotTime(session.counselling.endTime)}
                      </span>

                      <a
                        href={bookingDetail.booking?.program?.counsellingLink || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full sm:w-auto text-center uppercase px-5 py-2 bg-[#B4804A] hover:bg-[#9a6c3d] text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
                      >
                        {t("bookingDetails.joinCall", "Join Call")}
                      </a>
                    </div>
                  </div>
                )}

                {/* 2. Group Session Item */}
                {session.startDate && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-5 bg-white rounded-2xl shadow-sm border border-gray-100 gap-4">
                    {/* Date Block */}
                    <div className="flex sm:flex-col items-center justify-between sm:justify-center sm:w-28 border-b sm:border-b-0 sm:border-r border-gray-100 pb-3 sm:pb-0 sm:pr-4 shrink-0">
                      <span className="text-2xl sm:text-3xl font-bold text-[#037897]">
                        {new Date(session.startDate ?? "").toLocaleDateString(
                          currentLocale,
                          { day: "numeric" }
                        )}
                      </span>
                      <span className="text-xs sm:text-sm font-semibold uppercase text-gray-500">
                        {new Date(session.startDate ?? "").toLocaleDateString(
                          currentLocale,
                          { month: "short" }
                        )}
                      </span>
                    </div>

                    {/* Middle Content */}
                    <div className="flex-1 sm:px-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-[#EBF7F9] text-[#037897]">
                          {t("bookingDetails.liveSession", "Live Session")}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-[#041B44]">
                        {session.title}
                      </h3>
                      <div className="text-xs sm:text-sm text-[#78909C]">
                        <ReadMoreComponent
                          text={session?.description ?? ""}
                          maxLength={90}
                        />
                      </div>
                    </div>

                    {/* Time & Join Button */}
                    <div className="flex sm:flex-col items-center justify-between sm:justify-center sm:w-44 border-t sm:border-t-0 sm:border-l border-gray-100 pt-3 sm:pt-0 sm:pl-4 gap-3 shrink-0">
                      <span className="text-xs sm:text-sm font-semibold text-gray-700">
                        {formatSlotTime(session.startTime)}{" "}
                        -{" "}
                        {formatSlotTime(session.endTime)}
                      </span>

                      <a
                        href={bookingDetail.booking?.program?.zoomLink || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full sm:w-auto text-center uppercase px-5 py-2 bg-[#037897] hover:bg-[#025c74] text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
                      >
                        {t("bookingDetails.joinZoom", "Join Zoom")}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Section 4: Program Summary & Download Receipt Card */}
        {bookingDetail.booking && typeof bookingDetail.booking === "object" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8 flex flex-col-reverse md:flex-row items-center gap-8">
            <div className="w-full md:w-1/2 flex flex-col items-start">
              <h2 className="font-bold text-xl sm:text-2xl md:text-3xl text-[#020220] tracking-tight mb-3">
                {(bookingDetail.booking as BookingLists)?.program?.title ||
                  t("bookingDetails.programDetails", "Program Details")}
              </h2>

              <div className="text-[#546E7A] text-sm sm:text-base leading-relaxed mb-6">
                <ReadMoreComponent
                  text={
                    (bookingDetail.booking as BookingLists)?.program
                      ?.description ?? ""
                  }
                  maxLength={250}
                />
              </div>

              <button
                onClick={handleButtonClick}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-[#037897] hover:bg-[#025c74] text-white font-bold rounded-xl shadow-md transition-all active:scale-98 text-sm sm:text-base"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 25 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.2493 15.6445L12.2493 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.7959 12.0835L12.2494 15.6446L8.70293 12.0835"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.8847 6.75635H19.0195C21.4945 6.75635 23.5 8.76189 23.5 11.2381L23.5 17.1781C23.5 19.647 21.4993 21.6477 19.0304 21.6477L5.48176 21.6477C3.00676 21.6477 1 19.6409 1 17.1659L1 11.2247C1 8.75702 3.00189 6.75635 5.46959 6.75635L6.61527 6.75635"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("bookingDetails.downloadReceipt", "Download Receipt (PDF)")}
              </button>
            </div>

            <div className="w-full md:w-1/2">
              <img
                src={
                  (bookingDetail.booking as BookingLists)?.program?.icon ||
                  "/images/innerb.jpg"
                }
                alt="Program Cover"
                className="w-full h-48 sm:h-64 md:h-72 object-cover rounded-xl shadow-sm"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/images/innerb.jpg";
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
